import { useEffect, useRef, useState } from 'react';
import ReactEcharts from 'echarts-for-react';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import {
  formatChartXAxisValue,
  formatChartYAxisValue,
} from '../ChartOptions/Format/NumberFormatter';
import { palette } from '../..';
import { formatChartLabelValueForSelectedMeasure } from '../ChartOptions/Format/NumberFormatter';
import { RootState } from '../../redux';
import { useDispatch, useSelector } from 'react-redux';
import EmptyQuery from './EmptyQuery';
import { initializeWaterfallState } from '../../redux/ChartPoperties/ChartControlsActions';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import { tooltipStyle } from '../Common/EChartTooltip';
import {
  checkUidPresenceInCheckedMeasureForTooltip,
  limitChartData,
  shouldChartDisplayWithSecurity,
} from '../CommonFunctions/CommonFunctions';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import {
  LABEL_THRESHOLDS,
  AXIS_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';

interface Props {
  propKey: string;
  graphDimension?: any;
  chartArea?: any;
  graphTileSize?: number;
  colorScheme?: string;
  softUI?: boolean;
  fontFamily?: string;
}

const WaterfallChart = ({
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
  softUI,
}: Props) => {
  const {
    chartControls,
    chartProperties,
    tabTileProps,
    tabState,
    selectedTab,
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    chartType,
  } = useSelector((state: RootState) => ({
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
    tabTileProps: state.tabTileProps,
    tabState: state.tabState,
    selectedTab: state.tabTileProps.selectedTabId,
    tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
    chartType: state.chartProperties?.properties[propKey]?.chartType,
  }));

  const currentControls = chartControls.properties[propKey].waterfallChartControls || {};
  const {
    manualColor,
    positiveColor,
    negativeColor,
    totalColor,
    nodeValuesColors,
    allSeriesColor,
  } = currentControls;

  const dispatch = useDispatch();
  const [highlightedIndices, setHighlightedIndices] = useState<number[] | null>(null);
  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  var chartControl: ChartControlsProps = chartControls.properties[propKey];
  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];
  const isRight: boolean = chartControls.properties[propKey]?.waterfall?.total?.isRight;
  const isEnable: boolean = chartControls.properties[propKey]?.waterfall?.total?.isEnable;
  const grandTotalName: string = chartControls.properties[propKey]?.waterfall?.total?.name;

  const numDimensions = chartProperties.properties[propKey].chartAxes[1].fields.length;
  const dimensionKeys = chartData[0] && Object.keys(chartData[0]).slice(0, numDimensions);
  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  // added to clamp values based on min max axis settings
  const getClampedValue = (val: any, axisSettings: any) => {
    if (val === null || val === undefined || isNaN(val)) return val;
    let finalVal = val;
    if (!axisSettings) return val;

    if (axisSettings.enableMax && axisSettings.maxValue !== null && val > axisSettings.maxValue) {
      finalVal = axisSettings.maxValue;
    }
    if (axisSettings.enableMin && axisSettings.minValue !== null && val < axisSettings.minValue) {
      finalVal = axisSettings.minValue;
    }
    return finalVal;
  };

  useEffect(() => {
    if (
      chartControls.properties[propKey] &&
      chartProperties.properties[propKey]?.chartType === 'waterfall'
    ) {
      dispatch(initializeWaterfallState(propKey));
    }
  }, []);

  const limitedChartData = limitChartData(chartData, 100, chartAxes[2]?.fields[0]?.displayname);

  useEffect(() => {
    if (chartData.length > 100 && !tabTileProps.showDash) {
      toast.warning(`${messages.chart.excessiveData} 100 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  const processedChartData = limitedChartData.map((item) => {
    return Object.fromEntries(
      Object.entries(item).map(([key, value]) => [
        key,
        value === null
          ? '(Blank)'
          : typeof value === 'boolean'
            ? value
              ? 'True'
              : 'False'
            : value,
      ]),
    );
  });

  const xAxisName = (processedChartData[0] && Object.keys(processedChartData[0])[0]) || '';
  const yAxisName = (processedChartData[0] && Object.keys(processedChartData[0])[1]) || '';
  const xAxisValues = processedChartData.map((data) => data[xAxisName]);
  const yAxisValues = processedChartData.map((data) => Number(data[yAxisName]));

  const chartOriginalData = [...yAxisValues];

  const transparentPart: number[] = [];
  const chartDataForPositiveDirection: (string | number)[] = [];
  const chartDataForNegativeDirection: (string | number)[] = [];

  function getWaterfallData(data: number[]) {
    const EMPTY_DATA = '-';
    // for 0th index item
    transparentPart.push(0);
    if (data[0] >= 0) {
      chartDataForPositiveDirection.push(data[0]);
      chartDataForNegativeDirection.push(EMPTY_DATA);
    } else {
      chartDataForPositiveDirection.push(EMPTY_DATA);
      chartDataForNegativeDirection.push(data[0]);
    }

    let prevCumulativeSum = data[0];
    let currCumulativeSum = data[0];

    for (let i = 1; i < data.length; i++) {
      currCumulativeSum += data[i];

      if (prevCumulativeSum <= 0) {
        if (currCumulativeSum <= 0) {
          // Case negative - negative
          if (currCumulativeSum < prevCumulativeSum) {
            // went down (ex data- [-500, -100])
            transparentPart.push(prevCumulativeSum);
            chartDataForPositiveDirection.push(EMPTY_DATA);
            chartDataForNegativeDirection.push(data[i]);
          } else {
            // went up  (ex data- [-500, 200])
            transparentPart.push(currCumulativeSum);
            chartDataForPositiveDirection.push(-data[i]);
            chartDataForNegativeDirection.push(EMPTY_DATA);
          }
        } else {
          // Case negative - positive (ex data- [-500, 600]) (here 0 axis is being crossed)
          transparentPart.push(0);
          chartDataForPositiveDirection.push(currCumulativeSum);
          chartDataForNegativeDirection.push(prevCumulativeSum);
        }
      } else {
        if (currCumulativeSum <= 0) {
          // Case positive - negative  (ex data- [500, -600]) (here 0 axis is being crossed)
          transparentPart.push(0);
          chartDataForPositiveDirection.push(prevCumulativeSum);
          chartDataForNegativeDirection.push(currCumulativeSum);
        } else {
          // Case positive - positive
          if (currCumulativeSum > prevCumulativeSum) {
            // went up      (ex data- [500, 500])
            transparentPart.push(prevCumulativeSum);
            chartDataForPositiveDirection.push(data[i]);
            chartDataForNegativeDirection.push(EMPTY_DATA);
          } else {
            // went down   (ex data- [500, -100])
            transparentPart.push(currCumulativeSum);
            chartDataForPositiveDirection.push(EMPTY_DATA);
            chartDataForNegativeDirection.push(-data[i]);
          }
        }
      }
      prevCumulativeSum = currCumulativeSum;
    }
  }

  chartOriginalData && getWaterfallData(chartOriginalData);

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];
  const themeColors = selectedTheme?.colors || [];
  const finalPosColor = manualColor && positiveColor ? positiveColor : themeColors[0];
  const finalNegColor = manualColor && negativeColor ? negativeColor : themeColors[1];
  const finalTotalColor = manualColor && totalColor ? totalColor : themeColors[2] || themeColors[0];
  // isEnable defines whether to show grandTotal or not, isRight defines which side (left/right) the grand total will be shown.
  const chartTotal = chartOriginalData?.reduce((acc, curr) => {
    return acc + curr;
  }, 0);

  (function () {
    if (isEnable) {
      if (isRight) {
        xAxisValues.push(grandTotalName);
        chartOriginalData.push(chartTotal);
        transparentPart.push(0);
        if (chartTotal > 0) {
          chartDataForPositiveDirection.push(chartTotal);
          chartDataForNegativeDirection.push('-');
        } else {
          chartDataForNegativeDirection.push(chartTotal);
          chartDataForPositiveDirection.push('-');
        }
      } else {
        xAxisValues.unshift(grandTotalName);
        chartOriginalData.unshift(chartTotal);
        transparentPart.unshift(0);
        if (chartTotal > 0) {
          chartDataForPositiveDirection.unshift(chartTotal);
          chartDataForNegativeDirection.unshift('-');
        } else {
          chartDataForNegativeDirection.unshift(chartTotal);
          chartDataForPositiveDirection.unshift('-');
        }
      }
    }
  })();

  const getHeightAndWidth = () => {
    var height = 0;
    var width = 0;
    if (graphDimension.height > 230 && graphDimension.width > 350 && graphDimension.height < 300) {
      height = 12;
      width = 12;
    } else {
      if (!tabTileProps.showDash) {
        height = chartControl.legendOptions?.symbolHeight;
        width = chartControl.legendOptions?.symbolWidth;
      } else {
        height = 15;
        width = 15;
      }
    }
    return { height: height, width: width };
  };

  const getLegentShowValue = () => {
    var show = false;
    if (Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.waterfall.h) {
      show = chartControl.legendOptions?.showLegend;
    } else {
      show = false;
    }
    return show;
  };

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    if (tabState.tabs[selectedTab].hoveredPropkey === propKey || !mouseOverInteractivity) {
      return;
    }
    const timeout = setTimeout(() => {
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      let matchingIndices = processedChartData
        .map((item, index) => {
          const isMatch = tabState.tabs[selectedTab].hoveredItems?.some(
            (hovered: Record<string, any>) =>
              String(hovered[dimensionKeys[0]]) === String(item[dimensionKeys[0]]),
          );
          return isMatch ? index : -1;
        })
        .filter((index) => index !== -1);

      setHighlightedIndices(matchingIndices);
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);
  const hoveredRef = useRef<Record<string, any> | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (
        params?.componentIndex !== 0 && // To prevent hover on transparent part
        params?.seriesName &&
        params?.name &&
        hoveredRef.current?.[dimensionKeys[0]] !== params.name
      ) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry = { [dimensionKeys[0]]: params.name };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some((item) => item[dimensionKeys[0]] === params.name);
            if (!isDuplicate) {
              return [...prev, newEntry];
            }
            return prev;
          });
          // For highlighting in current waterfall chart
          setHighlightedIndices([params.dataIndex]);
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.name) {
        hoveredRef.current = null;
        setHoveredColumns([]);
        // For removing highlight in current waterfall chart
        setHighlightedIndices(null);
      }
    });
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const chartInstance = chartRef.current?.getEchartsInstance?.();
  const currentOption = chartInstance?.getOption();
  const [currentLegendSelected, setCurrentLegendSelected] = useState<any>(
    currentOption?.legend?.[0]?.selected ?? {},
  );

  const option = {
    color: selectedTheme?.colors[0] || [],
    backgroundColor: selectedTheme?.background || [],
    animation: false,
    fontFamily: fontFamily,
    tooltip: {
      show: isTooltipEnabled,
      useHTML: true,
      trigger: 'item',
      backgroundColor: 'white',
      borderWidth: 1,
      padding: 0,
      textStyle: {
        color: '#333',
        fontSize: 16,
        fontFamily: fontFamily,
      },
      appendToBody: true,
      extraCssText:
        'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
      formatter: function (params: any) {
        if (params.name === grandTotalName && isEnable) {
          //----------- this is for total bar tooltip(no dimension, measure and extra measure with their values) --------------//
          const totalData: { [key: string]: string | number } = {};

          const measureAxis = chartAxes.find((field: any) => field.name === 'Measure');
          const selectedMeasureKey = measureAxis.fields[0].displayname;

          if (selectedMeasureKey) {
            totalData[selectedMeasureKey] = chartTotal;
          }

          const extraFieldTotals: { [key: string]: number } = {};
          tooltipFormats.extraMeasureFieldsResponses.forEach((obj: any) => {
            Object.entries(obj).forEach(([key, val]) => {
              if (key !== xAxisName) {
                extraFieldTotals[key] = (extraFieldTotals[key] || 0) + Number(val);
              }
            });
          });

          Object.entries(extraFieldTotals).forEach(([key, val]) => {
            totalData[key] = val;
          });
          return tooltipStyle(
            params.color,
            totalData,
            tooltipFormats,
            chartAxes,
            undefined,
            undefined,
            undefined,
            fontFamily,
          );
        }
        //----- Adjust dataIndex when total is at beginning---//
        const adjustedDataIndex = isEnable && !isRight ? params.dataIndex - 1 : params.dataIndex;
        const row: any = processedChartData[adjustedDataIndex];
        const tempData = row;
        const dimensionKey = xAxisName;
        const dimensionValue = tempData[dimensionKey as string];
        const _data: { [key: string]: string | number } = {};
        //----------remove unchecked measure or any other field value of type date inside tooltip----------//
        Object.keys(tempData).forEach((key: string) => {
          const resp = checkUidPresenceInCheckedMeasureForTooltip(
            chartAxes,
            key,
            tooltipFormats?.checkedMeasure,
          );
          if (resp) {
            _data[key] = tempData[key];
          }
        });
        let mergedData = { ..._data };
        const extraResponses = tooltipFormats.extraMeasureFieldsResponses;
        if (tooltipFormats && extraResponses.length > 0) {
          const extraFieldObj = extraResponses.find(
            (obj: any) => String(obj[dimensionKey as string]) === String(dimensionValue as string),
          );
          if (extraFieldObj) {
            // remove dimension key value pair
            const { [dimensionKey as string]: _, ...restExtraFields } = extraFieldObj;
            //-----remove unchecked measure field after adding extra measure ----//
            let filteredFields = { ...mergedData, ...restExtraFields };
            const measureKey = chartAxes.find((axis: any) => axis.name === 'Measure').fields[0]
              .displayname;
            const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
              chartAxes,
              measureKey,
              tooltipFormats?.checkedMeasure,
            );
            if (!isPresent) {
              delete filteredFields[measureKey];
            }
            //send final response in tooltip data i.e checked measure plus extra fields
            mergedData = { ...filteredFields };
          }
        }
        const dimensionAxis = chartAxes[1];
        const dimensionDisplayNames = (dimensionAxis?.fields || []).map(
          (field: any) => field.displayname,
        );

        return tooltipStyle(
          params.color,
          mergedData,
          tooltipFormats,
          chartAxes,
          dimensionDisplayNames,
          undefined,
          undefined,
          fontFamily,
        );
      },
    },
    legend: {
      type: 'scroll',
      show: getLegentShowValue(),
      itemHeight: getHeightAndWidth().height,
      itemWidth: getHeightAndWidth().width,
      itemGap: chartControl.legendOptions?.itemGap,
      left: chartControl.legendOptions?.position?.left,
      top:
        chartControl.legendOptions?.position?.top !== 'bottom'
          ? chartControl.legendOptions?.position?.top
          : null,
      bottom: chartControl.legendOptions?.position?.top === 'bottom' ? 0 : null,
      orient: chartControl.legendOptions?.orientation,
      selected: currentLegendSelected,
      textStyle: {
        color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
        fontFamily: fontFamily,
      },
    },
    grid: {
      left:
        Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
          ? 5
          : chartControl.chartMargin.left + 5 + '%',
      right:
        Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
          ? 5
          : chartControl.chartMargin.right + '%',
      top:
        Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
          ? 5
          : chartControl.legendOptions?.position?.top === 'top'
            ? chartControl.chartMargin.top + 5 + '%'
            : chartControl.chartMargin.top + '%',
      bottom:
        Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
          ? 5
          : chartControl.legendOptions?.position?.top === 'bottom'
            ? (graphDimension.height * chartControl.chartMargin.bottom) / 100 + 35
            : chartControl.chartMargin.bottom + '%',
      shadowColor: 'rgba(0, 0, 0, 0.5)',
      shadowBlur: 10,
      shadowOffsetX: 3,
      shadowOffsetY: 3,
    },
    xAxis: {
      type: 'category',
      splitLine: {
        show: chartControl.axisOptions?.xSplitLine,
        lineStyle: {
          color: chartControl.axisOptions.xAxis[1]?.splitLineColor || '#d3d3d3',
          width: chartControl.axisOptions.xAxis[1]?.splitLineWidth ?? 1,
          type: chartControl.axisOptions.xAxis[1]?.splitLineType || 'solid',
        },
      },
      data: [...xAxisValues],
      axisLine: {
        show:
          chartControl.axisOptions.xAxis[1].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        onZero:
          chartControl.axisOptions.xAxis[1].position === 'top'
            ? false
            : chartControl.axisOptions.xAxis[1].onZero,
      },
      position: chartControl.axisOptions.xAxis[1].position,
      axisTick: {
        show:
          chartControl.axisOptions.xAxis[1].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        alignWithLabel: true,
        length:
          chartControl.axisOptions.xAxis[1].position === 'top'
            ? chartControl.axisOptions.xAxis[1].tickSizeTop
            : chartControl.axisOptions.xAxis[1].tickSizeBottom,
      },
      show: true,

      name:
        Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
          ? chartControl.axisOptions.xAxis[1].name
          : '',
      nameLocation: chartControl.axisOptions.xAxis[1].nameLocation,
      nameGap: chartControl.axisOptions.xAxis[1].nameGap,
      nameTextStyle: {
        fontSize: chartControl.axisOptions.xAxis[1].nameSize,
        color: chartControl.axisOptions.xAxis[1].nameColor,
      },
      axisLabel: {
        show:
          chartControl.axisOptions.xAxis[1].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        fontSize: chartControl.axisOptions.xAxis[1].fontSize,
        rotate:
          chartControl.axisOptions.xAxis[1].position === 'top'
            ? chartControl.axisOptions.xAxis[1].tickRotationTop
            : chartControl.axisOptions.xAxis[1].tickRotationBottom,
        margin:
          chartControl.axisOptions.xAxis[1].position === 'top'
            ? chartControl.axisOptions.xAxis[1].tickPaddingTop
            : chartControl.axisOptions.xAxis[1].tickPaddingBottom,
        color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
        fontFamily: fontFamily,
        formatter: (value: any) => {
          const fieldInAxes = chartAxes
            ?.flatMap((axis: any) => axis?.fields || [])
            ?.find((f: any) => f.displayname === xAxisName);

          const measureUid = fieldInAxes?.uId;

          const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
            measureUid
          ] as any;

          if (labelFormat?.finalDateFormat) {
            return formatChartXAxisValue(chartControl, value, labelFormat);
          }

          return value;
        },
      },
    },
    yAxis: {
      type: 'value',
      splitLine: {
        show: chartControl.axisOptions?.ySplitLine,
        lineStyle: {
          color: chartControl.axisOptions.yAxis[1]?.splitLineColor || '#d3d3d3',
          width: chartControl.axisOptions.yAxis[1]?.splitLineWidth ?? 1,
          type: chartControl.axisOptions.yAxis[1]?.splitLineType || 'solid',
        },
      },
      show: true,
      axisTick: {
        show:
          chartControl.axisOptions.yAxis[1].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        alignWithLabel: true,
        length:
          chartControl.axisOptions.yAxis[1].position === 'left'
            ? chartControl.axisOptions.yAxis[1].tickSizeLeft
            : chartControl.axisOptions.yAxis[1].tickSizeRight,
      },

      min: null,
      max: chartControl.axisOptions.axisMinMax.enableMax
        ? chartControl.axisOptions.axisMinMax.maxValue
        : null,

      inverse: chartControl.axisOptions.inverse,

      position: chartControl.axisOptions.yAxis[1].position,

      axisLine: {
        show:
          chartControl.axisOptions.yAxis[1].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        onZero: chartControl.axisOptions.yAxis[1].onZero,
      },
      axisLabel: {
        show:
          chartControl.axisOptions.yAxis[1].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        fontSize: chartControl.axisOptions.yAxis[1].fontSize,
        rotate:
          chartControl.axisOptions.yAxis[1].position === 'left'
            ? chartControl.axisOptions.yAxis[1].tickRotationLeft
            : chartControl.axisOptions.yAxis[1].tickRotationRight,
        margin:
          chartControl.axisOptions.yAxis[1].position === 'left'
            ? chartControl.axisOptions.yAxis[1].tickPaddingLeft
            : chartControl.axisOptions.yAxis[1].tickPaddingRight,
        color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
        fontFamily: fontFamily,
        formatter: (value: number) => {
          var formattedValue = formatChartYAxisValue(chartControl, value);
          return formattedValue;
        },
      },
      name:
        Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
          ? chartControl.axisOptions.yAxis[1].name
          : '',
      nameLocation: chartControl.axisOptions.yAxis[1].nameLocation,
      nameGap: chartControl.axisOptions.yAxis[1].nameGap,
      nameTextStyle: {
        fontSize: chartControl.axisOptions.yAxis[1].nameSize,
        color: chartControl.axisOptions.yAxis[1].nameColor,
      },
    },
    series: [
      {
        type: 'bar',
        stack: 'Total',
        itemStyle: {
          borderColor: 'transparent',
          color: 'transparent',
        },
        emphasis: {
          itemStyle: {
            borderColor: 'transparent',
            color: 'transparent',
          },
          disabled: true,
        },
        tooltip: {
          show: false,
        },
        cursor: 'default',
        data: [...transparentPart],
      },
      {
        // name: yAxisName,
        name: 'Positive',
        type: 'bar',
        stack: 'Total',
        emphasis: {
          disabled: true,
        },
        data: chartDataForPositiveDirection.map((item, index) => {
          if (item === '-') return '-';

          const axisMinMax = chartControl.axisOptions.axisMinMax;
          const clampedVal = typeof item === 'number' ? getClampedValue(item, axisMinMax) : item;

          const isTotalBar =
            isEnable &&
            ((isRight && index === chartDataForPositiveDirection.length - 1) ||
              (!isRight && index === 0));

          const dimensionName = String(xAxisValues[index]);
          let barColor = finalPosColor;

          if (manualColor) {
            if (nodeValuesColors && nodeValuesColors[dimensionName]) {
              barColor = nodeValuesColors[dimensionName];
            } else if (
              (dimensionName === 'null' || dimensionName === '(Blank)') &&
              nodeValuesColors &&
              nodeValuesColors['null']
            ) {
              barColor = nodeValuesColors['null'];
            } else if (isTotalBar) {
              barColor = finalTotalColor;
            } else if (allSeriesColor) {
              barColor = allSeriesColor;
            } else if (chartOriginalData[index] < 0) {
              barColor = finalNegColor;
            }
          } else {
            barColor =
              chartOriginalData[index] > 0 ? selectedTheme?.colors[0] : selectedTheme?.colors[1];
          }

          return {
            value: clampedVal,
            itemStyle: {
              color: barColor,
              opacity:
                !highlightedIndices ||
                !highlightedIndices.length ||
                highlightedIndices?.includes(index)
                  ? 1
                  : 0.5,
            },
          };
        }),
        itemStyle: softUI
          ? {
              shadowColor: 'rgba(0, 0, 0, 0.5)', // Shadow color
              shadowBlur: 10, // Blurring effect
              shadowOffsetX: 3, // Horizontal shadow displacement
              shadowOffsetY: 3, // Vertical shadow displacement
            }
          : {},
        label: {
          show:
            Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
              ? chartControl.labelOptions.showLabel
              : false,
          fontSize: chartControl.labelOptions.fontSize,
          fontFamily: fontFamily,
          color: chartControl.labelOptions.labelColor
            ? chartControl.labelOptions.labelColor
            : selectedTheme?.dark
              ? '#ffffff'
              : '#757474',
          backgroundColor: chartControl.labelOptions.labelBackground
            ? chartControl.labelOptions.labelBackgroundColor
            : 'transparent',
          borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
          padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
          formatter: (params: any) => {
            let valueToFormat;
            if (
              chartDataForPositiveDirection[params.dataIndex] !== '-' &&
              chartDataForNegativeDirection[params.dataIndex] !== '-'
            ) {
              // crosses x axis
              if (
                Math.abs(chartDataForPositiveDirection[params.dataIndex] as number) >
                Math.abs(chartDataForNegativeDirection[params.dataIndex] as number)
              ) {
                valueToFormat = chartOriginalData[params.dataIndex];
              } else {
                valueToFormat = '-';
              }
            } else {
              valueToFormat = chartOriginalData[params.dataIndex];
            }
            return valueToFormat !== '-'
              ? formatChartLabelValueForSelectedMeasure(
                  chartControls.properties[propKey],
                  chartProperties.properties[propKey],
                  valueToFormat,
                  yAxisName,
                )
              : '';
          },
        },
      },
      {
        name: 'Negative',
        // name: yAxisName,
        type: 'bar',
        stack: 'Total',
        emphasis: {
          disabled: true,
        },
        data: chartDataForNegativeDirection.map((item, index) => {
          if (item === '-') return '-';

          const axisMinMax = chartControl.axisOptions.axisMinMax;
          const clampedVal = typeof item === 'number' ? getClampedValue(item, axisMinMax) : item;

          const isTotalBar =
            isEnable &&
            ((isRight && index === chartDataForNegativeDirection.length - 1) ||
              (!isRight && index === 0));

          const dimensionName = String(xAxisValues[index]);
          let barColor = finalNegColor;

          if (manualColor) {
            if (nodeValuesColors && nodeValuesColors[dimensionName]) {
              barColor = nodeValuesColors[dimensionName];
            } else if (
              (dimensionName === 'null' || dimensionName === '(Blank)') &&
              nodeValuesColors &&
              nodeValuesColors['null']
            ) {
              barColor = nodeValuesColors['null'];
            } else if (isTotalBar) {
              barColor = finalTotalColor;
            } else if (allSeriesColor) {
              barColor = allSeriesColor;
            } else if (chartOriginalData[index] > 0) {
              barColor = finalPosColor;
            }
          } else {
            barColor =
              chartOriginalData[index] > 0 ? selectedTheme?.colors[0] : selectedTheme?.colors[1];
          }

          return {
            value: clampedVal,
            itemStyle: {
              color: barColor,
              opacity:
                !highlightedIndices ||
                !highlightedIndices.length ||
                highlightedIndices?.includes(index)
                  ? 1
                  : 0.5,
            },
          };
        }),
        itemStyle: softUI
          ? {
              shadowColor: 'rgba(0, 0, 0, 0.5)', // Shadow color
              shadowBlur: 10, // Blurring effect
              shadowOffsetX: 3, // Horizontal shadow displacement
              shadowOffsetY: 3, // Vertical shadow displacement
            }
          : { color: selectedTheme?.colors[1] },
        label: {
          show:
            Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
              ? chartControl.labelOptions.showLabel
              : false,
          fontSize: chartControl.labelOptions.fontSize,
          fontFamily: fontFamily,
          color: chartControl.labelOptions.labelColor
            ? chartControl.labelOptions.labelColor
            : selectedTheme?.dark
              ? '#ffffff'
              : '#757474',
          backgroundColor: chartControl.labelOptions.labelBackground
            ? chartControl.labelOptions.labelBackgroundColor
            : 'transparent',
          borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
          padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
          formatter: (params: any) => {
            let valueToFormat;
            if (
              chartDataForPositiveDirection[params.dataIndex] !== '-' &&
              chartDataForNegativeDirection[params.dataIndex] !== '-'
            ) {
              // crosses x axis
              if (
                Math.abs(chartDataForPositiveDirection[params.dataIndex] as number) <
                Math.abs(chartDataForNegativeDirection[params.dataIndex] as number)
              ) {
                valueToFormat = chartOriginalData[params.dataIndex];
              } else {
                valueToFormat = '-';
              }
            } else {
              valueToFormat = chartOriginalData[params.dataIndex];
            }
            return valueToFormat !== '-'
              ? formatChartLabelValueForSelectedMeasure(
                  chartControls.properties[propKey],
                  chartProperties.properties[propKey],
                  valueToFormat,
                  yAxisName,
                )
              : '';
          },
        },
      },
    ],
  };

  const onLegendSelectChanged = (params: any) => {
    setCurrentLegendSelected(params.selected);
  };

  const RenderChart = () => {
    return chartData ? (
      <>
        <ReactEcharts
          opts={{ renderer: 'svg' }}
          ref={chartRef}
          onChartReady={handleChartReady}
          onEvents={{
            legendselectchanged: onLegendSelectChanged,
          }}
          style={{
            width: graphDimension.width,
            height: graphDimension.height,
            overflow: 'hidden',
            margin: 'auto',
            border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
          }}
          option={option}
        />
      </>
    ) : null;
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};

export default WaterfallChart;
